import LegalLayout, {
  LegalSection,
  InfoList,
  Columns,
  Subhead,
  Bullets,
  Note,
} from "@/components/ttt/LegalLayout"
import type { TttChrome } from "@/components/ttt/types"

interface Props {
  chrome: TttChrome
  lastUpdated: string
}

const mail = "turbotimetravel@gmail.com"

export default function Cgv({ chrome, lastUpdated }: Props) {
  return (
    <LegalLayout
      chrome={chrome}
      title="Conditions Générales de Vente"
      subtitle="Conditions générales de vente applicables aux livres et produits culturels vendus par TTT Corp"
      lastUpdated={lastUpdated}
      cta={{
        title: "Questions sur nos conditions de vente ?",
        subtitle: "Notre équipe est disponible pour répondre à vos questions",
        secondaryHref: chrome.paths.mentions_legales,
        secondaryLabel: "Mentions légales",
      }}
    >
      <LegalSection title="1. Présentation de l'association">
        <InfoList
          items={[
            { label: "Dénomination", value: "TTT Corp" },
            { label: "Président", value: "Yann Cavalier" },
            { label: "RNA", value: "W442019112" },
            {
              label: "Siège social",
              value: (
                <>
                  14 Rue Sanlecque
                  <br />
                  44000 Nantes, France
                </>
              ),
            },
            {
              label: "Contact",
              value: (
                <a href={`mailto:${mail}`} className="text-primary hover:underline">
                  {mail}
                </a>
              ),
            },
            { label: "Service client", value: "Par email uniquement" },
          ]}
        />
      </LegalSection>

      <LegalSection title="2. Objet et champ d'application">
        <p>
          Les présentes Conditions Générales de Vente (CGV) régissent les relations contractuelles
          entre TTT Corp et ses clients pour la vente de livres et produits culturels via le site
          ttt-editions.fr.
        </p>
        <Subhead>Produits proposés</Subhead>
        <Bullets
          items={[
            "Livres édités par TTT Corp",
            "Ouvrages de création littéraire et artistique",
            "Produits culturels associés",
            "Éditions numériques et physiques",
          ]}
        />
      </LegalSection>

      <LegalSection title="3. Commandes et disponibilité">
        <Columns>
          <div>
            <Subhead>Commande simple</Subhead>
            <p className="text-content/80 text-sm">Processus de commande intuitif et sécurisé.</p>
          </div>
          <div>
            <Subhead>Stock limité</Subhead>
            <p className="text-content/80 text-sm">Vente sous réserve de disponibilité des stocks.</p>
          </div>
        </Columns>
        <p>
          Les commandes sont traitées dans l'ordre d'arrivée. En cas d'indisponibilité d'un article,
          le client sera informé dans les plus brefs délais.
        </p>
      </LegalSection>

      <LegalSection title="4. Prix et modalités de paiement">
        <Columns>
          <div>
            <Subhead>Paiement par Stripe</Subhead>
            <p className="text-content/80 text-sm">
              Tous les paiements s'effectuent de manière sécurisée via la plateforme Stripe (cartes
              bancaires, Paypal, etc.)
            </p>
          </div>
          <div>
            <Subhead>Prix TTC</Subhead>
            <p className="text-content/80 text-sm">
              Tous nos prix sont affichés toutes taxes comprises.
            </p>
          </div>
        </Columns>
        <Note title="Association à but non lucratif">
          Chiffre d'affaires annuel ≈ 1000€.
        </Note>
        <p>
          Les prix peuvent être modifiés à tout moment, mais les commandes en cours sont honorées aux
          conditions du moment de la commande. Les frais de livraison sont calculés selon les tarifs
          des transporteurs pour l'Europe.
        </p>
      </LegalSection>

      <LegalSection title="5. Livraison et expédition">
        <p>
          TTT Corp expédie ses produits en Europe selon les modalités définies par les transporteurs
          partenaires.
        </p>
        <Columns>
          <div>
            <Subhead>Zone de livraison</Subhead>
            <Bullets
              items={[
                "Union Européenne",
                "Suisse et Norvège",
                "Tarifs selon transporteur",
                "Délais variables selon destination",
              ]}
            />
          </div>
          <div>
            <Subhead>Modalités d'expédition</Subhead>
            <Bullets
              items={[
                "Préparation sous 2-5 jours ouvrés",
                "Suivi de commande par email",
                "Emballage soigné et écologique",
                "Assurance transport incluse",
              ]}
            />
          </div>
        </Columns>
      </LegalSection>

      <LegalSection title="6. Droit de rétractation">
        <Subhead>Droit de rétractation de 14 jours</Subhead>
        <p>
          Conformément à la législation française, vous disposez d'un délai de 14 jours pour retourner
          vos achats.
        </p>
        <Columns>
          <div>
            <Subhead>Conditions de retour</Subhead>
            <Bullets
              items={[
                "Produit en parfait état",
                "Emballage d'origine conservé",
                "Retour dans les 14 jours",
                "Frais de retour à votre charge",
              ]}
            />
          </div>
          <div>
            <Subhead>Procédure</Subhead>
            <Bullets
              items={[
                <>
                  Contactez{" "}
                  <a href={`mailto:${mail}`} className="text-primary hover:underline">
                    {mail}
                  </a>
                </>,
                "Obtenez un numéro de retour",
                "Renvoyez le produit avec facture",
                "Remboursement sous 14 jours",
              ]}
            />
          </div>
        </Columns>
        <Note title="Clause importante">
          Les livres doivent être retournés dans leur état d'origine. Les frais de retour restent à la
          charge de l'acheteur sauf en cas de produit défectueux.
        </Note>
      </LegalSection>

      <LegalSection title="7. Propriété intellectuelle">
        <p>
          Tous les ouvrages édités par TTT Corp sont protégés par les droits de propriété
          intellectuelle et d'auteur.
        </p>
        <Columns>
          <div>
            <Subhead>Droits de l'acheteur</Subhead>
            <Bullets
              items={[
                "Usage personnel et privé",
                "Lecture et consultation",
                "Prêt dans le cercle familial",
                "Conservation personnelle",
              ]}
            />
          </div>
          <div>
            <Subhead>Interdictions</Subhead>
            <Bullets
              items={[
                "Reproduction et copie",
                "Diffusion publique",
                "Revente ou redistribution",
                "Usage commercial",
              ]}
            />
          </div>
        </Columns>
        <Note title="Respect du droit d'auteur">
          L'achat d'un livre vous donne le droit de le lire et de le conserver, mais ne vous transfère
          aucun droit de propriété intellectuelle sur l'œuvre.
        </Note>
      </LegalSection>

      <LegalSection title="8. Responsabilité et garanties">
        <p>
          TTT Corp s'engage à fournir des produits conformes et de qualité dans le respect de son
          statut associatif.
        </p>
        <Columns>
          <div>
            <Subhead>Garantie qualité</Subhead>
            <p className="text-content/80 text-sm">Produits neufs et en parfait état.</p>
          </div>
          <div>
            <Subhead>Service client</Subhead>
            <p className="text-content/80 text-sm">Support par email uniquement.</p>
          </div>
          <div>
            <Subhead>Limitation</Subhead>
            <p className="text-content/80 text-sm">Responsabilité limitée au statut associatif.</p>
          </div>
        </Columns>
      </LegalSection>

      <LegalSection title="9. Protection des données personnelles">
        <Subhead>Conformité RGPD</Subhead>
        <p>
          TTT Corp respecte la réglementation européenne sur la protection des données personnelles.
        </p>
        <p>
          Vos données personnelles (nom, adresse, email) sont collectées uniquement dans le cadre de
          la gestion de votre commande et ne sont jamais partagées avec des tiers.
        </p>
        <Note>
          <a href={chrome.paths.confidentiality} className="text-primary hover:underline">
            Consulter notre politique de confidentialité
          </a>
        </Note>
      </LegalSection>

      <LegalSection title="10. Droit applicable et juridiction compétente">
        <Columns>
          <div>
            <Subhead>Droit applicable</Subhead>
            <p className="text-content/80 text-sm">
              Les présentes CGV sont soumises au droit français et au droit des associations.
            </p>
          </div>
          <div>
            <Subhead>Juridiction compétente</Subhead>
            <p className="text-content/80 text-sm">
              En cas de litige, les tribunaux de Nantes seront seuls compétents.
            </p>
          </div>
        </Columns>
      </LegalSection>
    </LegalLayout>
  )
}
